import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';




// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add token to requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('gigship_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Handle response errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401) {
            const url = originalRequest?.url || '';
            const isAuthRoute =
                url.includes('/login') ||
                url.includes('/auth/') ||
                url.includes('/request-password-reset') ||
                url.includes('/reset-password');

            if (!isAuthRoute && !originalRequest?._retry) {
                originalRequest._retry = true;
                console.warn('🔒 401 on protected route:', url, '— deferring to AuthContext handler');
            }
        }
        return Promise.reject(error);
    }
);

const orderService = {
    // Create new order
    createOrder: async (orderData) => {
        try {
            const response = await api.post('/orders', orderData);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to create order' };
        }
    },

    // Get user orders
    getUserOrders: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();

            if (params.role) queryParams.append('role', params.role);
            if (params.status) queryParams.append('status', params.status);
            if (params.page) queryParams.append('page', params.page);
            if (params.limit) queryParams.append('limit', params.limit);
            // GET /orders pages with skip (it has no page parameter)
            if (params.skip) queryParams.append('skip', params.skip);
            if (params.sortBy) queryParams.append('sortBy', params.sortBy);
            if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

            const response = await api.get(`/orders?${queryParams.toString()}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch orders' };
        }
    },

    // Get single order by ID
    getOrderById: async (orderId) => {
        try {
            const response = await api.get(`/orders/${orderId}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch order' };
        }
    },

    // Pay an order that is awaiting payment from the wallet into escrow (Buyer) — POST /orders/:id/pay
    payOrder: async (orderId) => {
        try {
            const response = await api.post(`/orders/${orderId}/pay`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Payment failed' };
        }
    },

    // Start working on order (Seller)
    startOrder: async (orderId) => {
        try {
            const response = await api.patch(`/orders/${orderId}/start`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to start order' };
        }
    },

    // Deliver order (Seller)
    deliverOrder: async (orderId, deliveryData) => {
        try {
            // The route reads the delivered file URLs from `files`; callers may pass them as `attachments`.
            const { attachments, ...rest } = deliveryData || {};
            const response = await api.post(`/orders/${orderId}/deliver`, { ...rest, files: rest.files ?? attachments ?? [] });
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to deliver order' };
        }
    },

    // Request revision (Buyer)
    requestRevision: async (orderId, reason) => {
        try {
            const response = await api.post(`/orders/${orderId}/revision`, { reason });
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to request revision' };
        }
    },

    // Accept revision (Seller)
    acceptRevision: async (orderId) => {
        try {
            const response = await api.post(`/orders/${orderId}/revision/accept`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to accept revision' };
        }
    },

    // Accept delivery (Buyer)
    acceptDelivery: async (orderId) => {
        try {
            const response = await api.post(`/orders/${orderId}/accept`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to accept delivery' };
        }
    },

    // Request cancellation
    requestCancellation: async (orderId, reason) => {
        try {
            const response = await api.post(`/orders/${orderId}/cancellation`, { reason });
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to request cancellation' };
        }
    },

    // Approve cancellation
    approveCancellation: async (orderId) => {
        try {
            const response = await api.patch(`/orders/${orderId}/cancellation/approve`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to approve cancellation' };
        }
    },

    // Deny cancellation
    denyCancellation: async (orderId) => {
        try {
            const response = await api.patch(`/orders/${orderId}/cancellation/deny`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to deny cancellation' };
        }
    },

    // Raise dispute
    raiseDispute: async (orderId, disputeData) => {
        try {
            const response = await api.post('/disputes/create', {
                orderId,
                ...disputeData
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to raise dispute' };
        }
    },

    // Get order statistics
    getOrderStatistics: async (role = 'buyer') => {
        try {
            const response = await api.get(`/orders/stats?role=${role}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch order statistics' };
        }
    },

    // Upload delivery files
    uploadDeliveryFiles: async (files) => {
        try {
            const formData = new FormData();

            files.forEach((file) => {
                formData.append('files', file);
            });

            // Note: The content-type header for multipart/form-data is usually handled automatically 
            // by axios when passing a FormData object, but explicitly setting it to override the 
            // default 'application/json' set globally is safer.
            const response = await api.post('/upload/delivery', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to upload files' };
        }
    },

    // Reorder a previous order (Priority 6: One-Click Reorder)
    reorderOrder: async (orderId) => {
        try {
            const response = await api.post(`/orders/reorder/${orderId}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to prepare reorder' };
        }
    },

    // ── Type 1 Extension ─────────────────────────────────────────────
    // Gigger marks arrival — starts 5-min free grace period (Maps ETA-aware)
    markGiggerArrival: async (orderId, giggerLocation) => {
        try {
            const response = await api.post(`/orders/${orderId}/waiting-time/start`, { giggerLocation });
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to mark arrival' };
        }
    },

    // ── Type 2 Extension ─────────────────────────────────────────────
    // Shipper requests in-course time extension (min 30 mins)
    requestTimeExtension: async (orderId, { additionalMinutes, reason }) => {
        try {
            const response = await api.post(`/orders/${orderId}/extensions/request`, {
                additionalMinutes,
                reason
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to request extension' };
        }
    },

    // Gigger responds to a time extension request
    respondToExtension: async (orderId, extId, status) => {
        try {
            const response = await api.patch(`/orders/${orderId}/extensions/${extId}/respond`, { status });
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to respond to extension' };
        }
    }
};

export default orderService;